import { getActions, getGlobal, setGlobal } from '../global';

import type { ApiChat, ApiChatInviteImporter, ApiUser, ApiUserFullInfo } from '../api/types';
import { MAIN_THREAD_ID } from '../api/types';

import {
  SUPPORTED_AUDIO_CONTENT_TYPES,
  SUPPORTED_PHOTO_CONTENT_TYPES,
  SUPPORTED_VIDEO_CONTENT_TYPES,
} from '../config';
import { isChatChannel, isChatGroup, isChatSuperGroup } from '../global/helpers/chats';
import { selectChat, selectCurrentChat, selectUser, selectUserFullInfo, selectUserStatus } from '../global/selectors';
import { isUserId } from '../util/entities/ids';
import { callApi } from '../api/gramjs';
import buildAttachment from '../components/middle/composer/helpers/buildAttachment';

// ==========================================
// Broadcast Types
// ==========================================

export type BroadcastTargetMode =
  | 'selected' // 用户选择的群+联系人
  | 'all_groups' // 所有群（排除）
  | 'all_contacts' // 所有联系人（排除）
  | 'group_members' // 某个群的全部成员
  | 'all_chats' // 所有联系人和群组
  | 'phone_numbers'; // 手机号码（空格分隔的陌生号码，一次性 import 解析）

export type BroadcastTarget = {
  mode: BroadcastTargetMode;
  chatIds?: string[]; // mode=selected 时使用
  excludeChatIds?: string[]; // 排除的 ID
  fromGroupId?: string; // mode=group_members 时使用
  excludeUserIds?: string[]; // mode=group_members 时使用排除用户
  phoneNumbers?: string; // mode=phone_numbers 时使用（原始字符串，空格/换行/逗号分隔）
};

// 群发手机号码上限（与服务端 contacts.ImportContacts 性能 + 实际场景折中）
export const MAX_PHONE_NUMBERS_PER_BROADCAST = 100;

/**
 * 解析手机号码字符串：支持空格 / 换行 / 逗号 / 中文逗号 分隔。
 * 去除每个号码的非数字字符、去重、最多保留 MAX_PHONE_NUMBERS_PER_BROADCAST 个。
 */
export function parsePhoneNumbers(raw: string): string[] {
  if (!raw) return [];
  const list = raw
    .split(/[\s\n,，]+/)
    .map((s) => s.replace(/\D/g, ''))
    .filter(Boolean);
  const dedup = Array.from(new Set(list));
  return dedup.slice(0, MAX_PHONE_NUMBERS_PER_BROADCAST);
}

export type BroadcastContentType = 'text' | 'media' | 'voice_base64' | 'image_text';

export type BroadcastContent =
  | { type: 'text'; text: string }
  | {
    type: 'media';
    files: Array<{ file: Blob | File | string; filename?: string; base64?: string; fileType?: string }>;
    caption?: string;
  }
  | { type: 'voice_base64'; base64Data: string }
  | {
    type: 'image_text';
    file: { file: Blob | File | string; filename?: string; base64?: string; fileType?: string };
    text: string;
  };

export type BroadcastInterval = {
  minMs: number;
  maxMs: number;
};

export type BroadcastConfig = {
  startAt?: number | Date;
  target: BroadcastTarget;
  content: BroadcastContent;
  interval: BroadcastInterval;
  isSilent?: boolean;
  onProgress?: (current: number, total: number, chatId: string, result: { ok: boolean; reason?: string }) => void;
};

export type BroadcastTask = {
  id: number;
  status: 'pending' | 'running' | 'completed' | 'cancelled' | 'expired';
  config: BroadcastConfig;
  startTime: number;
  progress: { current: number; total: number; results: Array<{ chatId: string; ok: boolean; reason?: string }> };
  timeoutId?: number;
  cancel: () => void;
};

// localStorage key
const BROADCAST_STORAGE_KEY = 'telegram_exposed_broadcast_tasks';

// File to base64 helper
function fileToBase64(file: File): Promise<{ name: string; type: string; data: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({
        name: file.name,
        type: file.type,
        data: reader.result as string,
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Base64 to Blob helper
function base64ToBlob(base64Data: string, mimeType: string): Blob {
  const byteCharacters = atob(base64Data.split(',')[1] || base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

// Convert stored content with base64 files back to usable content
async function restoreContentWithFiles(content: BroadcastContent): Promise<BroadcastContent> {
  if (content.type === 'text') {
    return content;
  }

  if (content.type === 'voice_base64') {
    return content;
  }

  // For media content, convert base64 back to File objects
  if (content.type === 'media' && content.files && content.files.length > 0) {
    const restoredFiles = await Promise.all(
      // eslint-disable-next-line @typescript-eslint/require-await
      content.files.map(async (f) => {
        if (f.base64) {
          const blob = base64ToBlob(f.base64, f.fileType || 'application/octet-stream');
          const filename = f.filename || 'file';
          const file = new File([blob], filename, { type: f.fileType || 'application/octet-stream' });
          return { file, filename };
        }
        return undefined;
      }),
    );

    const validFiles = restoredFiles.filter((f): f is { file: File; filename: string } => Boolean(f));

    if (validFiles.length === 0) {
      throw new Error('媒体文件数据丢失，请重新选择文件');
    }

    return { type: 'media', files: validFiles, caption: content.caption };
  }

  // For image_text content, convert base64 back to File object
  if (content.type === 'image_text' && content.file) {
    const f = content.file;
    if (f.base64) {
      const blob = base64ToBlob(f.base64, f.fileType || 'application/octet-stream');
      const filename = f.filename || 'image';
      const file = new File([blob], filename, { type: f.fileType || 'application/octet-stream' });
      return { type: 'image_text', file: { file, filename }, text: content.text };
    }
    throw new Error('图片文件数据丢失，请重新选择文件');
  }

  return { type: 'text', text: '' };
}

// Save tasks to localStorage (async to handle file conversion)
async function saveTasksToStorageAsync() {
  const tasks: Array<{
    id: number;
    status: string;
    startTime: number;
    scheduledTime?: number;
    target: BroadcastTarget;
    content: BroadcastContent & { files?: Array<{ base64?: string; fileType?: string; filename: string }> };
    interval: BroadcastInterval;
    isSilent?: boolean;
  }> = [];

  // Process each pending task
  const processTasks = async () => {
    for (const task of broadcastTasks.values()) {
      if (task.status === 'pending') {
        let scheduledTime: number | undefined;
        if (task.config.startAt) {
          scheduledTime = task.config.startAt instanceof Date
            ? task.config.startAt.getTime()
            : task.config.startAt;
        }

        // Convert files to base64 if present
        let savedContent: {
          type: 'text'; text: string;
        } | {
          type: 'media'; files: Array<{ base64: string; fileType: string; filename: string }>; caption?: string;
        } | {
          type: 'voice_base64'; base64Data: string;
        } | {
          type: 'image_text'; file: { base64: string; fileType: string; filename: string }; text: string;
        };

        if (task.config.content.type === 'media' && task.config.content.files && task.config.content.files.length > 0) {
          const fileBase64s: Array<{ base64: string; fileType: string; filename: string }> = [];
          for (const f of task.config.content.files) {
            const base64 = await fileToBase64(f.file as File);
            const fileType = (f.file instanceof File || f.file instanceof Blob)
              ? f.file.type
              : 'application/octet-stream';
            fileBase64s.push({
              base64: base64.data,
              fileType,
              filename: f.filename || 'file',
            });
          }
          savedContent = {
            type: 'media',
            files: fileBase64s,
            caption: task.config.content.caption,
          };
        } else if (task.config.content.type === 'image_text' && task.config.content.file) {
          const f = task.config.content.file;
          const base64 = await fileToBase64(f.file as File);
          const fileType = (f.file instanceof File || f.file instanceof Blob)
            ? f.file.type
            : 'application/octet-stream';
          savedContent = {
            type: 'image_text',
            file: { base64: base64.data, fileType, filename: f.filename || 'image' },
            text: task.config.content.text,
          };
        } else {
          // For text or voice_base64 types, use assertion
          savedContent = task.config.content as typeof savedContent;
        }

        tasks.push({
          id: task.id,
          status: task.status,
          startTime: task.startTime,
          scheduledTime,
          target: task.config.target,
          content: savedContent as BroadcastContent
          & { files?: Array<{ base64?: string; fileType?: string; filename: string }> },
          interval: task.config.interval,
          isSilent: task.config.isSilent,
        });
      }
    }
  };

  await processTasks();

  try {
    localStorage.setItem(BROADCAST_STORAGE_KEY, JSON.stringify(tasks));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to save broadcast tasks:', e);
  }
}

// Load tasks from localStorage and resume pending tasks
async function loadTasksFromStorage(onProgress?: (taskId: number, current: number, total: number) => void) {
  try {
    const stored = localStorage.getItem(BROADCAST_STORAGE_KEY);
    if (!stored) return;

    const tasks = JSON.parse(stored) as Array<{
      id: number;
      status: string;
      startTime: number;
      scheduledTime?: number;
      target: BroadcastTarget;
      content: BroadcastContent & { files?: Array<{ base64?: string; fileType?: string; filename: string }> };
      interval: BroadcastInterval;
      isSilent?: boolean;
    }>;

    const now = Date.now();

    for (const storedTask of tasks) {
      // 检查是否过期
      if (storedTask.scheduledTime && storedTask.scheduledTime < now) {
        // 已过期，跳过（会在后面删除）
        continue;
      }

      // 重建任务
      const id = storedTask.id;
      let cancelled = false;

      // Restore content with files from base64
      let restoredContent: BroadcastContent;
      try {
        restoredContent = await restoreContentWithFiles(storedTask.content as BroadcastContent);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to restore content:', err);
        // Skip this task if files are missing
        continue;
      }

      const task: BroadcastTask = {
        id,
        status: 'pending',
        config: {
          startAt: storedTask.scheduledTime,
          target: storedTask.target,
          content: restoredContent,
          interval: storedTask.interval,
          isSilent: storedTask.isSilent,
          onProgress: undefined, // 加载时无法恢复回调
        },
        startTime: storedTask.startTime,
        progress: { current: 0, total: 0, results: [] },
        cancel: () => {
          cancelled = true;
          // 如果还在等待 setTimeout 触发，立即取消。
          if (task.timeoutId !== undefined) {
            clearTimeout(task.timeoutId);
            task.timeoutId = undefined;
          }
          // 立刻把状态标记为 cancelled，使 UI 能立即反映。
          if (task.status === 'pending') {
            task.status = 'cancelled';
          }
        },
      };

      // 计算目标列表
      const chatIds = await resolveBroadcastChatIds(task.config.target);
      task.progress.total = chatIds.length;

      // 延迟执行
      const startAtMs = storedTask.scheduledTime || now;
      const delay = Math.max(0, startAtMs - now);

      const runBroadcast = async () => {
        task.status = 'running';
        task.timeoutId = undefined;
        await saveTasksToStorageAsync(); // 更新存储（移除 pending）

        for (let i = 0; i < chatIds.length; i += 1) {
          if (cancelled) {
            task.status = 'cancelled';
            break;
          }

          const chatId = chatIds[i];
          task.progress.current = i + 1;

          // 发送
          const result = await sendContentToChat(chatId, task.config.content, task.config.isSilent);
          task.progress.results.push({ chatId, ok: result.ok, reason: result.reason });

          // 回调（加载时可能没有回调）
          if (task.config.onProgress) {
            task.config.onProgress(i + 1, chatIds.length, chatId, result);
          }
          if (onProgress) {
            onProgress(id, i + 1, chatIds.length);
          }

          // 随机间隔
          if (i < chatIds.length - 1 && !cancelled) {
            const waitMs = randomInt(task.config.interval.minMs, task.config.interval.maxMs);
            await new Promise((r) => setTimeout(r, waitMs));
          }
        }

        if (!cancelled) {
          task.status = 'completed';
        }
      };

      broadcastTasks.set(id, task);

      // 启动延时，并保留 timeoutId 以便取消时 clearTimeout
      const timeoutId = window.setTimeout(() => {
        runBroadcast();
      }, delay);
      task.timeoutId = timeoutId;
    }

    // 清理过期任务
    const validTasks = tasks.filter((t) => !t.scheduledTime || t.scheduledTime >= now);
    if (validTasks.length !== tasks.length) {
      localStorage.setItem(BROADCAST_STORAGE_KEY, JSON.stringify(validTasks));
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to load broadcast tasks:', e);
  }
}

// Initialize broadcast tasks from storage
function initBroadcastTasks() {
  loadTasksFromStorage();
}

// ==========================================
// Helper Functions
// ==========================================

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * 轮询等待 userIds 在 global.chats.byId 出现，最长等 maxMs。
 * 返回到时仍未出现的 userId 列表。
 */
async function waitForChatsById(userIds: string[], maxMs: number): Promise<string[]> {
  const start = Date.now();
  const interval = 50;
  while (Date.now() - start < maxMs) {
    const global = getGlobal();
    const byId = global.chats?.byId || {};
    const missing = userIds.filter((id) => !byId[id]);
    if (missing.length === 0) return [];
    await new Promise((r) => setTimeout(r, interval));
  }
  const global = getGlobal();
  const byId = global.chats?.byId || {};
  return userIds.filter((id) => !byId[id]);
}

/**
 * 兜底：手动构造最小 ApiChat 直接写入 global.chats.byId。
 * 仅在 fetchChat 触发的 worker update 因节流等原因未及时落库时使用。
 */
function writeChatsByIdDirectly(missingUserIds: string[], apiUsers: ApiUser[]): void {
  let global = getGlobal();
  const byId = { ...(global.chats?.byId || {}) };
  for (const uid of missingUserIds) {
    const user = apiUsers.find((u) => u.id === uid);
    byId[uid] = {
      id: uid,
      type: 'chatTypePrivate',
      title: uid, // 无用户信息时用 userId；正常路径走 fetchChat，有正确姓名
      accessHash: user?.accessHash,
    } as ApiChat;
  }
  global = {
    ...global,
    chats: {
      ...global.chats,
      byId,
    },
  };
  setGlobal(global);
}

async function resolveBroadcastChatIds(target: BroadcastTarget): Promise<string[]> {
  const global = getGlobal();
  const allChats = global.chats?.byId || {};
  const allUsers = global.users?.byId || {};
  const allChatIds = Object.keys(allChats);

  const excludeSet = new Set(target.excludeChatIds || []);

  switch (target.mode) {
    case 'selected':
      return (target.chatIds || []).filter((id) => !excludeSet.has(id));

    case 'all_groups': {
      // 包括 Basic Group 和 Super Group，不包括 Channel
      return allChatIds.filter((id) => {
        const chat = allChats[id] as ApiChat | undefined;
        if (!chat) return false;
        const isGroup = isChatGroup(chat);
        return isGroup && !excludeSet.has(id);
      });
    }

    case 'all_contacts': {
      // 所有是联系人的私聊
      return allChatIds.filter((id) => {
        if (excludeSet.has(id)) return false;
        const chat = allChats[id] as ApiChat | undefined;
        const user = allUsers[id] as ApiUser | undefined;
        if (!chat || !user) return false;
        // 是私聊且是联系人
        return chat.type === 'chatTypePrivate' && user.isContact === true;
      });
    }

    case 'all_chats': {
      return allChatIds.filter((id) => !excludeSet.has(id));
    }

    case 'group_members': {
      if (!target.fromGroupId) return [];
      const group = allChats[target.fromGroupId] as ApiChat | undefined;
      if (!group) return [];
      const fullInfo = global.chats?.fullInfoById?.[target.fromGroupId];
      const members = fullInfo?.members || [];
      const excludeUsersSet = new Set(target.excludeUserIds || []);

      const userIds = members
        .filter((m) => m.userId && !excludeUsersSet.has(m.userId))
        .map((m) => m.userId);

      // userId 就是私聊的 chatId（对于普通用户）
      return userIds.filter((uid) => !excludeSet.has(uid) && isUserId(uid));
    }

    case 'phone_numbers': {
      // 通过 contacts.ImportContacts 一次性把陌生手机号解析为 userId。
      // 注意：该 API 副作用会把对方加入当前账号的联系人列表（无法避免）。
      const phones = parsePhoneNumbers(target.phoneNumbers || '');
      if (phones.length === 0) return [];

      const result = await callApi('fetchImportedContacts', {
        contacts: phones.map((phone, index) => ({
          clientId: String(index + 1),
          phone,
          firstName: '',
          lastName: '',
        })),
      });
      if (!result || !result.importedContacts.length) return [];

      const apiUsers = result.users;

      // 过滤掉未注册的 phone，并去重 userId
      const allUserIds = Array.from(new Set(
        result.importedContacts
          .map((imp) => imp.userId)
          .filter((uid) => uid && isUserId(uid) && !excludeSet.has(uid)),
      ));

      // 已存在于 byId 的 userId：chat 已有，title 正确，不需要任何写入
      const existingChatIds = global.chats?.byId ? Object.keys(global.chats.byId) : [];
      const alreadyInById = new Set(allUserIds.filter((uid) => existingChatIds.includes(uid)));
      // 只对"确实还没有 chat"的 userId 触发 fetchChat（同步路径）
      const needFetch = allUserIds.filter((uid) => !alreadyInById.has(uid));

      await Promise.all(needFetch.map(async (userId) => {
        const user = apiUsers.find((u) => u.id === userId);
        if (!user) return;
        try {
          await callApi('fetchChat', { type: 'user', user });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[resolveBroadcastChatIds] fetchChat failed for', userId, err);
        }
      }));

      // 轮询等待 fetchChat 触发的 updateChat 在主线程真正落库。
      // 对已在 byId 的 chat 跳过轮询（已确认存在）。
      const missing = await waitForChatsById(needFetch, 2000);
      if (missing.length > 0) {
        // eslint-disable-next-line no-console
        console.warn('[resolveBroadcastChatIds] worker update not received for', missing,
          'falling back to manual setGlobal');
        writeChatsByIdDirectly(missing, apiUsers);
      }

      return allUserIds;
    }

    default:
      return [];
  }
}

// 发送内容到单个 chatId
async function sendContentToChat(
  chatId: string,
  content: BroadcastContent,
  isSilent?: boolean,
): Promise<{ ok: boolean; reason?: string }> {
  try {
    const global = getGlobal();
    const chat = selectChat(global, chatId);
    if (!chat) {
      return { ok: false, reason: 'chat_not_found' };
    }

    switch (content.type) {
      case 'text':
        return await api.sendText({ chatId, text: content.text, isSilent });

      case 'media': {
        const result = await api.sendMedia({
          chatId,
          files: content.files,
          caption: content.caption,
          isSilent,
        });
        return result;
      }

      case 'image_text': {
        const result = await api.sendMedia({
          chatId,
          files: [content.file],
          caption: content.text,
          isSilent,
        });
        return result;
      }

      case 'voice_base64': {
        // 使用已有的发送逻辑，先解码 base64
        const { base64Data } = content;
        let mimeType = 'audio/ogg';
        const base64 = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;
        const mimeMatch = base64Data.match(/^data:([^;]+);base64,/);
        if (mimeMatch) {
          mimeType = mimeMatch[1];
        }
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i += 1) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: mimeType });

        // 计算 waveform
        let duration = 0;
        let waveform: number[] = [];
        try {
          const waveResult = await computeWaveform(blob);
          duration = waveResult.duration;
          waveform = waveResult.waveform;
        } catch {
          // continue without waveform
        }

        const attachment = await buildAttachment('voice.ogg', blob, { voice: { duration, waveform } });
        const actions = getActions();
        const messageList = { chatId, threadId: MAIN_THREAD_ID, type: 'thread' } as any;
        actions.sendMessage({ messageList, attachments: [attachment], isSilent });
        return { ok: true };
      }

      default:
        return { ok: false, reason: 'unsupported_content' };
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('sendContentToChat error', err);
    return { ok: false, reason: 'exception' };
  }
}

// ==========================================
// Broadcast State
// ==========================================

let nextBroadcastId = 1;
const broadcastTasks = new Map<number, BroadcastTask>();

// ==========================================
// Modal Event System (for UI integration)
// ==========================================

type ModalCallback = (isOpen: boolean) => void;
const modalListeners = new Set<ModalCallback>();

export function addBroadcastModalListener(callback: ModalCallback) {
  modalListeners.add(callback);
  return () => {
    modalListeners.delete(callback);
  };
}

function notifyBroadcastModal(isOpen: boolean) {
  modalListeners.forEach((cb) => cb(isOpen));
}

// ==========================================
// API Object
// ==========================================

async function computeWaveform(blob: Blob, bins = 63): Promise<{ duration: number; waveform: number[] }> {
  try {
    const arrayBuffer = await blob.arrayBuffer();
    const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) throw new Error('AudioContext unsupported');
    const ctx = new AudioCtx();
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
    const channelData = audioBuffer.numberOfChannels > 0 ? audioBuffer.getChannelData(0) : undefined;
    const duration = Math.round(audioBuffer.duration || 0);
    if (!channelData || channelData.length === 0) {
      try {
        ctx.close?.();
      } catch (e) { /* empty */ }
      return { duration, waveform: [] };
    }

    const len = channelData.length;
    const step = Math.max(1, Math.floor(len / bins));
    const waveform: number[] = [];
    let maxVal = 0;
    for (let i = 0; i < len; i += step) {
      let sum = 0;
      const end = Math.min(i + step, len);
      for (let j = i; j < end; j += 1) {
        const v = Math.abs(channelData[j]);
        sum += v;
      }
      const avg = sum / (end - i);
      waveform.push(avg);
      if (avg > maxVal) maxVal = avg;
    }
    // normalize to 0..255
    const normalized = waveform.map((v) => {
      const n = maxVal > 0 ? Math.round((v / maxVal) * 255) : 0;
      return n;
    });
    try {
      ctx.close?.();
    } catch (e) { /* empty */
    }
    return { duration, waveform: normalized };
  } catch (err) {
    // fallback
    return { duration: 0, waveform: [] };
  }
}

type SendTextArgs = {
  chatId: string;
  threadId?: number | string;
  text: string;
  isSilent?: boolean;
};

const api = {
  /**
   * Send a simple text message to a chat.
   * If the chat is not present in global state, this will return { ok: false, reason: 'chat_not_found' } immediately.
   *
   * Returns a Promise resolving to { ok: boolean, reason?: string }.
   */
  sendText: ({ chatId, threadId = MAIN_THREAD_ID, text, isSilent }: SendTextArgs) => {
    try {
      const actions = getActions();
      const global = getGlobal();
      const chat = selectChat(global, chatId);

      if (!chat) {
        // 关键：暴露真实错误，不要静默失败
        // eslint-disable-next-line no-console
        console.error('[globalApi.sendText] chat not found in global.chats.byId:', chatId);
        return Promise.resolve({ ok: false, reason: 'chat_not_found' });
      }

      // 诊断：检测是否在给"自己"发消息（Saved Messages 现象）。
      const currentUserId = global.currentUserId;
      const isSelf = chatId === currentUserId;
      // eslint-disable-next-line no-console
      console.log('[globalApi.sendText] target:', {
        chatId, title: chat.title, type: chat.type, isSelf, currentUserId,
      });

      const messageList = { chatId, threadId, type: 'thread' } as any;
      actions.sendMessage({ messageList, text, isSilent });
      return Promise.resolve({ ok: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[globalApi.sendText] exception:', err);
      const message = err instanceof Error ? err.message : 'unknown';
      return Promise.resolve({ ok: false, reason: 'exception', detail: message });
    }
  },

  /**
   * Send multiple text messages in sequence with a given interval (ms).
   * messages: Array of { chatId, threadId?, text, isSilent? }
   * intervalMs: delay between sends in milliseconds (default 500)
   *
   * Returns Promise resolving to array of results for each message.
   */
  sendBatch: async (messages: SendTextArgs[], intervalMs = 500) => {
    const results: Array<{ ok: boolean; reason?: string }> = [];
    for (let i = 0; i < messages.length; i += 1) {
      const res = await api.sendText(messages[i]);
      results.push(res as { ok: boolean; reason?: string });
      if (i < messages.length - 1) {
        await new Promise((r) => setTimeout(r, intervalMs));
      }
    }
    return results;
  },

  /**
   * Send a single file/blob/url as an attachment.
   * file: Blob | File | string (url)
   * filename optional: when file is Blob and filename not provided, a default is used.
   */
  sendFile: async (args: { chatId: string; file: Blob | File | string;
    filename?: string; threadId?: number | string; isSilent?: boolean; }) => {
    const { chatId, file, filename, threadId = MAIN_THREAD_ID, isSilent } = args;
    try {
      const global = getGlobal();
      const chat = selectChat(global, chatId);
      if (!chat) return { ok: false, reason: 'chat_not_found' };

      let blob: Blob;
      if (typeof file === 'string') {
        const resp = await fetch(file);
        if (!resp.ok) return { ok: false, reason: 'failed_fetch' };
        blob = await resp.blob();
      } else {
        blob = file;
      }

      // validation: size
      const MAX_SIZE = 50 * 1024 * 1024; // 50 MB
      if (blob.size > MAX_SIZE) return { ok: false, reason: 'file_too_large' };

      // validation: mime
      const mime = blob.type || '';
      const allowedMime = new Set<string>([
        ...Array.from(SUPPORTED_AUDIO_CONTENT_TYPES || []),
        ...Array.from(SUPPORTED_PHOTO_CONTENT_TYPES || []),
        ...Array.from(SUPPORTED_VIDEO_CONTENT_TYPES || []),
      ]);
      if (mime && !allowedMime.has(mime)) {
        return { ok: false, reason: 'unsupported_mime' };
      }

      const name = filename || (blob instanceof File ? blob.name : 'file.bin');
      let attachment;
      if (mime.startsWith('audio/')) {
        try {
          const { duration, waveform } = await computeWaveform(blob);
          attachment = await buildAttachment(name, blob, { voice: { duration, waveform } });
        } catch (e) {
          // fallback
          // eslint-disable-next-line no-console
          console.warn('computeWaveform failed', e);
          attachment = await buildAttachment(name, blob);
        }
      } else {
        attachment = await buildAttachment(name, blob);
      }

      const actions = getActions();
      const messageList = { chatId, threadId, type: 'thread' } as any;
      actions.sendMessage({ messageList, attachments: [attachment], isSilent });
      return { ok: true };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.sendFile error', err);
      return { ok: false, reason: 'exception' };
    }
  },

  /**
   * Get the current active chat information.
   * Returns the chat object if a chat is currently open, otherwise returns undefined.
   */
  getCurrentChatInfo: () => {
    try {
      const global = getGlobal();
      const chat = selectCurrentChat(global);
      if (!chat) return undefined;
      return chat;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.getCurrentChatInfo error', err);
      return undefined;
    }
  },

  /**
   * Get the current active chat ID.
   * Returns the chatId string if a chat is currently open, otherwise returns undefined.
   */
  getCurrentChatId: () => {
    try {
      const global = getGlobal();
      const chat = selectCurrentChat(global);
      if (!chat) return undefined;
      return chat.id;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.getCurrentChatId error', err);
      return undefined;
    }
  },

  /**
   * Get the userInfo of the person in the currently open private chat.
   * Returns an ApiUser object if the current chat is a private chat, otherwise returns undefined.
   */
  getCurrentPrivateChatUserInfo: (): ApiUser | undefined => {
    try {
      const global = getGlobal();
      const chat = selectCurrentChat(global);
      if (!chat) return undefined;
      if (chat.type !== 'chatTypePrivate') return undefined;
      return selectUser(global, chat.id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.getCurrentPrivateChatUserInfo error', err);
      return undefined;
    }
  },

  /**
   * Get userInfo by userId.
   * userId: the user id string.
   * Returns an ApiUser object if the user is found, otherwise returns undefined.
   */
  getUserInfoById: (userId: string): ApiUser | undefined => {
    try {
      const global = getGlobal();
      if (!userId) return undefined;
      return selectUser(global, userId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.getUserInfoById error', err);
      return undefined;
    }
  },

  /**
   * Get the extended userInfo of the person in the currently open private chat.
   * This includes bio, birthday, commonChatsCount, profilePhoto and more.
   * Note: the full info needs to be loaded first; use loadCurrentPrivateChatUserFullInfo to ensure it's fetched.
   */
  getCurrentPrivateChatUserFullInfo: (): ApiUserFullInfo | undefined => {
    try {
      const global = getGlobal();
      const chat = selectCurrentChat(global);
      if (!chat) return undefined;
      if (chat.type !== 'chatTypePrivate') return undefined;
      return selectUserFullInfo(global, chat.id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.getCurrentPrivateChatUserFullInfo error', err);
      return undefined;
    }
  },

  /**
   * Get extended userInfo by userId.
   * This includes bio, birthday, commonChatsCount, profilePhoto and more.
   * Note: the full info needs to be loaded first; use loadUserFullInfoById to ensure it's fetched.
   */
  getUserFullInfoById: (userId: string): ApiUserFullInfo | undefined => {
    try {
      const global = getGlobal();
      if (!userId) return undefined;
      return selectUserFullInfo(global, userId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.getUserFullInfoById error', err);
      return undefined;
    }
  },

  /**
   * Trigger loading of extended userInfo for the current private chat.
   * After calling this, use getCurrentPrivateChatUserFullInfo() to retrieve the loaded data.
   * Returns true if the request was initiated, false if the user or chat was not found.
   */
  loadCurrentPrivateChatUserFullInfo: (): boolean => {
    try {
      const global = getGlobal();
      const chat = selectCurrentChat(global);
      if (!chat) return false;
      if (chat.type !== 'chatTypePrivate') return false;
      const user = selectUser(global, chat.id);
      if (!user) return false;
      getActions().loadFullUser({ userId: chat.id });
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.loadCurrentPrivateChatUserFullInfo error', err);
      return false;
    }
  },

  /**
   * Trigger loading of extended userInfo by userId.
   * After calling this, use getUserFullInfoById() to retrieve the loaded data.
   * Returns true if the request was initiated, false if the user was not found.
   */
  loadUserFullInfoById: (userId: string): boolean => {
    try {
      if (!userId) return false;
      const global = getGlobal();
      const user = selectUser(global, userId);
      if (!user) return false;
      getActions().loadFullUser({ userId });
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.loadUserFullInfoById error', err);
      return false;
    }
  },

  /**
   * Get user online/offline status by userId.
   * Returns an ApiUserStatus object (type, wasOnline, expires).
   */
  getUserStatusById: (userId: string) => {
    try {
      const global = getGlobal();
      if (!userId) return undefined;
      return selectUserStatus(global, userId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.getUserStatusById error', err);
      return undefined;
    }
  },

  /**
   * Send a voice message (base64 audio) to the currently active chat.
   * base64Data: base64 encoded audio data (with or without data URL prefix)
   * Returns { ok: boolean, reason?: string }
   *
   * If no chat is currently open, returns { ok: false, reason: 'no_current_chat' }
   */
  sendVoiceToCurrentChat: async (args: { base64Data: string; isSilent?: boolean }) => {
    const { base64Data, isSilent } = args;
    try {
      const global = getGlobal();
      const chat = selectCurrentChat(global);

      if (!chat) {
        return { ok: false, reason: 'no_current_chat' };
      }

      const chatId = chat.id;

      // Convert base64 to Blob
      let mimeType = 'audio/ogg'; // default for voice messages

      // Remove data URL prefix if present
      const base64 = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;
      const mimeMatch = base64Data.match(/^data:([^;]+);base64,/);
      if (mimeMatch) {
        mimeType = mimeMatch[1];
      }

      // Decode base64 to binary
      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i += 1) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType });

      // Validation: size
      const MAX_SIZE = 50 * 1024 * 1024; // 50 MB
      if (blob.size > MAX_SIZE) return { ok: false, reason: 'file_too_large' };

      // Compute waveform and duration
      let duration = 0;
      let waveform: number[] = [];
      try {
        const result = await computeWaveform(blob);
        duration = result.duration;
        waveform = result.waveform;
      } catch (e) {
        // fallback - continue without waveform
        // eslint-disable-next-line no-console
        console.warn('computeWaveform failed for voice', e);
      }

      const filename = 'voice.ogg';
      const attachment = await buildAttachment(filename, blob, { voice: { duration, waveform } });

      const actions = getActions();
      const messageList = { chatId, threadId: MAIN_THREAD_ID, type: 'thread' } as any;
      actions.sendMessage({ messageList, attachments: [attachment], isSilent });
      return { ok: true };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.sendVoiceToCurrentChat error', err);
      return { ok: false, reason: 'exception' };
    }
  },

  /**
   * Get all available chats (groups and contacts) for selection.
   * Returns array of { id, title, type, isContact }
   */
  getAvailableChats: () => {
    try {
      const global = getGlobal();
      const allChats = global.chats?.byId || {};
      const allUsers = global.users?.byId || {};

      const result: Array<{
        id: string;
        title: string;
        type: 'group' | 'channel' | 'private' | 'secret';
        isContact?: boolean;
        isUser: boolean;
      }> = [];

      Object.values(allChats).forEach((chat: ApiChat) => {
        if (chat?.isNotJoined) {
          return;
        }
        let chatType: 'group' | 'channel' | 'private' | 'secret' = 'private';
        if (chat.type === 'chatTypeBasicGroup' || chat.type === 'chatTypeSuperGroup') {
          chatType = 'group';
        } else if (chat.type === 'chatTypeChannel') {
          chatType = 'channel';
        } else if (chat.type === 'chatTypeSecret') {
          chatType = 'secret';
        }

        const user = allUsers[chat.id] as ApiUser | undefined;
        result.push({
          id: chat.id,
          title: chat.title || 'Unknown',
          type: chatType,
          isContact: user?.isContact,
          isUser: chat.type === 'chatTypePrivate',
        });
      });

      return result;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.getAvailableChats error', err);
      return [];
    }
  },

  /**
   * Get all groups (for target selection).
   */
  getAllGroups: () => {
    try {
      const global = getGlobal();
      const allChats = global.chats?.byId || {};
      return Object.values(allChats)
        .filter((chat: ApiChat) => (isChatGroup(chat) || isChatChannel(chat)) && !chat.isNotJoined)
        .map((chat: ApiChat) => ({
          id: chat.id,
          title: chat.title || 'Unknown',
          isSuperGroup: isChatSuperGroup(chat),
          membersCount: chat.membersCount,
          isNotJoined: chat.isNotJoined,
        }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.getAllGroups error', err);
      return [];
    }
  },

  /**
   * Get all contacts (for target selection).
   */
  getAllContacts: () => {
    try {
      const global = getGlobal();
      const allUsers = global.users?.byId || {};

      return Object.values(allUsers)
        .filter((user: ApiUser) => user.isContact === true)
        .map((user: ApiUser) => {
          return {
            id: user.id,
            title: [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unknown',
          };
        });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.getAllContacts error', err);
      return [];
    }
  },

  /**
   * Get group members for a specific group.
   * Returns array of user IDs that are members of the group.
   */
  getGroupMembers: (groupId: string) => {
    try {
      const global = getGlobal();
      const fullInfo = global.chats?.fullInfoById?.[groupId];
      if (!fullInfo || !fullInfo.members) {
        return [];
      }
      return fullInfo.members.map((m) => m.userId).filter(Boolean);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.getGroupMembers error', err);
      return [];
    }
  },

  /**
   * Open the broadcast modal (for UI integration).
   * Components can listen to this via addBroadcastModalListener.
   */
  openBroadcastModal: () => {
    notifyBroadcastModal(true);
  },

  /**
   * Close the broadcast modal (for UI integration).
   */
  closeBroadcastModal: () => {
    notifyBroadcastModal(false);
  },

  /**
   * Get broadcast tasks status.
   */
  getBroadcastTasks: () => {
    const tasks: Array<{
      id: number;
      status: string;
      progress: { current: number; total: number };
      startTime: number;
      scheduledTime?: number;
      targetMode: string;
      targetCount: number;
      contentType: string;
      contentPreview: string;
    }> = [];
    broadcastTasks.forEach((task) => {
      // 定时列表只展示仍需关注的任务：待执行（pending）和执行中（running）。
      // 已取消 / 已完成 / 已过期（FLOOD 等）的任务从定时列表中移除。
      if (task.status !== 'pending' && task.status !== 'running') {
        return;
      }
      // 计算实际计划执行时间
      let scheduledTime: number | undefined;
      if (task.config.startAt) {
        scheduledTime = task.config.startAt instanceof Date
          ? task.config.startAt.getTime()
          : task.config.startAt;
      }

      // 内容预览
      let contentPreview = '';
      let contentType = '';
      if (task.config.content.type === 'text') {
        contentType = '文本';
        contentPreview = task.config.content.text.substring(0, 50);
        if (task.config.content.text.length > 50) contentPreview += '...';
      } else if (task.config.content.type === 'media') {
        contentType = '媒体';
        const fileCount = task.config.content.files?.length || 0;
        const caption = task.config.content.caption || '';
        contentPreview = `${fileCount} 个文件`;
        if (caption) {
          contentPreview += `: ${caption.substring(0, 30)}`;
          if (caption.length > 30) contentPreview += '...';
        }
      } else if (task.config.content.type === 'image_text') {
        contentType = '图片+文本';
        contentPreview = task.config.content.text.substring(0, 50);
        if (task.config.content.text.length > 50) contentPreview += '...';
      } else if (task.config.content.type === 'voice_base64') {
        contentType = '语音';
        contentPreview = '语音消息';
      }

      // 目标信息
      let targetMode = '';
      let targetCount = 0;
      if (task.config.target.mode === 'selected') {
        targetMode = '选中的';
        targetCount = task.config.target.chatIds?.length || 0;
      } else if (task.config.target.mode === 'all_groups') {
        targetMode = '所有群';
      } else if (task.config.target.mode === 'all_contacts') {
        targetMode = '所有联系人';
      } else if (task.config.target.mode === 'all_chats') {
        targetMode = '所有聊天';
      } else if (task.config.target.mode === 'group_members') {
        targetMode = '群成员';
        targetCount = task.config.target.fromGroupId ? 1 : 0;
      } else if (task.config.target.mode === 'phone_numbers') {
        targetMode = '手机号码';
        targetCount = parsePhoneNumbers(task.config.target.phoneNumbers || '').length;
      }

      tasks.push({
        id: task.id,
        status: task.status,
        progress: { current: task.progress.current, total: task.progress.total },
        startTime: task.startTime,
        scheduledTime,
        targetMode,
        targetCount,
        contentType,
        contentPreview,
      });
    });
    return tasks;
  },

  /**
   * Schedule a broadcast task.
   * Returns task id if successful.
   */
  scheduleBroadcast: async (config: BroadcastConfig) => {
    const id = nextBroadcastId++;

    let cancelled = false;

    const task: BroadcastTask = {
      id,
      status: 'pending',
      config,
      startTime: Date.now(),
      progress: { current: 0, total: 0, results: [] },
      cancel: () => {
        cancelled = true;
        // 如果还在等待 setTimeout 触发，立即取消，避免定时器到点后还启动发送。
        if (task.timeoutId !== undefined) {
          clearTimeout(task.timeoutId);
          task.timeoutId = undefined;
        }
        // 立刻把状态标记为 cancelled，这样 UI 调用 getBroadcastTasks
        // 后能立即看到取消按钮消失、状态变为"已取消"。
        if (task.status === 'pending') {
          task.status = 'cancelled';
        }
      },
    };

    broadcastTasks.set(id, task);

    // 保存到 localStorage (async)
    await saveTasksToStorageAsync();

    // 计算目标列表
    const chatIds = await resolveBroadcastChatIds(config.target);
    task.progress.total = chatIds.length;

    // 延迟执行
    const startAtMs = !config.startAt
      ? Date.now()
      : config.startAt instanceof Date
        ? config.startAt.getTime()
        : config.startAt;
    const delay = Math.max(0, startAtMs - Date.now());

    const runBroadcast = async () => {
      task.status = 'running';
      task.timeoutId = undefined;
      await saveTasksToStorageAsync(); // 更新存储（移除 pending）

      for (let i = 0; i < chatIds.length; i += 1) {
        if (cancelled) {
          task.status = 'cancelled';
          break;
        }

        const chatId = chatIds[i];
        task.progress.current = i + 1;

        // 发送
        const result = await sendContentToChat(chatId, config.content, config.isSilent);
        task.progress.results.push({ chatId, ok: result.ok, reason: result.reason });

        // 回调进度
        if (config.onProgress) {
          // eslint-disable-next-line no-console
          console.log('[globalApi] onProgress called:', i + 1, chatIds.length, chatId);
          config.onProgress(i + 1, chatIds.length, chatId, result);
        }

        // 遇到 FLOOD_WAIT 或连续发送异常，立即停止，避免被封更久
        const reason = (result.reason || '').toUpperCase();
        if (reason.includes('FLOOD') || reason === 'EXCEPTION' || reason === 'RATELIMIT') {
          // eslint-disable-next-line no-console
          console.error('[globalApi] hit flood/ratelimit, stopping broadcast. reason:', result.reason);
          task.status = 'expired';
          break;
        }

        // 随机间隔
        if (i < chatIds.length - 1 && !cancelled) {
          const waitMs = randomInt(config.interval.minMs, config.interval.maxMs);
          await new Promise((r) => setTimeout(r, waitMs));
        }
      }

      if (!cancelled) {
        task.status = 'completed';
      }
    };

    // 启动延时，并保留 timeoutId 以便取消时 clearTimeout
    const timeoutId = window.setTimeout(() => {
      runBroadcast();
    }, delay);
    task.timeoutId = timeoutId;

    // 如果在保存 task 后、设置 timeoutId 前已经被取消，立即清理。
    // 后续 cancel() 会通过 timeoutId 直接清理，无需再次检查。
    if (cancelled) {
      clearTimeout(timeoutId);
      task.timeoutId = undefined;
    }

    return { ok: true, taskId: id };
  },

  /**
   * Cancel a broadcast task.
   */
  cancelBroadcast: async (taskId: number) => {
    const task = broadcastTasks.get(taskId);
    if (!task) {
      return { ok: false, reason: 'not_found' };
    }
    task.cancel();
    // 更新 localStorage
    await saveTasksToStorageAsync();
    return { ok: true };
  },

  /**
   * Send multiple attachments as one grouped message (if more than 1).
   * files: array of { file: Blob|File|string(url), filename?: string }
   */
  sendMedia: async (args: { chatId: string; files: Array<{ file: Blob | File | string; filename?: string }>;
    caption?: string; threadId?: number | string; isSilent?: boolean; }) => {
    const { chatId, files, caption, threadId = MAIN_THREAD_ID, isSilent } = args;
    try {
      const global = getGlobal();
      const chat = selectChat(global, chatId);
      if (!chat) return { ok: false, reason: 'chat_not_found' };

      const attachments = [];
      for (let i = 0; i < files.length; i += 1) {
        const it = files[i];
        let blob: Blob;
        if (typeof it.file === 'string') {
          const resp = await fetch(it.file);
          if (!resp.ok) return { ok: false, reason: 'failed_fetch' };
          blob = await resp.blob();
        } else {
          blob = it.file;
        }
        // validation: size
        const MAX_SIZE = 50 * 1024 * 1024; // 50 MB
        if (blob.size > MAX_SIZE) return { ok: false, reason: 'file_too_large' };

        // validation: mime
        const mime = blob.type || '';
        const allowedMime = new Set<string>([
          ...Array.from(SUPPORTED_AUDIO_CONTENT_TYPES || []),
          ...Array.from(SUPPORTED_PHOTO_CONTENT_TYPES || []),
          ...Array.from(SUPPORTED_VIDEO_CONTENT_TYPES || []),
        ]);
        if (mime && !allowedMime.has(mime)) {
          return { ok: false, reason: 'unsupported_mime' };
        }

        const name = it.filename || (blob instanceof File ? blob.name : `file-${i}`);
        let attachment;
        if (mime.startsWith('audio/')) {
          try {
            const { duration, waveform } = await computeWaveform(blob);
            attachment = await buildAttachment(name, blob, { voice: { duration, waveform } });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('computeWaveform failed', e);
            attachment = await buildAttachment(name, blob);
          }
        } else {
          attachment = await buildAttachment(name, blob);
        }
        attachments.push(attachment);
      }

      const actions = getActions();
      const messageList = { chatId, threadId, type: 'thread' } as any;
      // 支持 caption（图片+文字）
      actions.sendMessage({
        messageList,
        text: caption,
        attachments,
        shouldGroupMessages: attachments.length > 1,
        isSilent,
      });
      return { ok: true };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.sendMedia error', err);
      return { ok: false, reason: 'exception' };
    }
  },

  /**
   * Initialize broadcast tasks from localStorage.
   * This is called automatically when the module loads.
   */
  initBroadcastTasks: () => {
    initBroadcastTasks();
  },

  /**
   * Get all members who joined a group via an invite link.
   * link: the invite link URL (e.g. "https://t.me/+ABC123" or "+ABC123").
   * limit: how many results to fetch per page (default 200, max recommended 200).
   * Returns { importers, total } on success, undefined on failure or if the chat is not found.
   */
  getChatMembersByLink: async (args: {
    chatId: string;
    link: string;
    limit?: number;
  }): Promise<{ importers: ApiChatInviteImporter[]; total: number } | undefined> => {
    const { chatId, link, limit = 200 } = args;
    try {
      const global = getGlobal();
      const chat = selectChat(global, chatId);
      if (!chat || !chat.accessHash) return undefined;

      const result = await callApi('fetchChatInviteImporters', {
        peer: chat,
        link,
        limit,
      });
      if (!result) return undefined;

      return {
        importers: result.importers,
        total: result.importers.length,
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.getChatMembersByLink error', err);
      return undefined;
    }
  },

  /**
   * Check which phone numbers are registered on Telegram and retrieve their user info.
   * phoneNumbers: a string of phone numbers separated by spaces, newlines, or commas.
   * Returns an array of { phone, userId, firstName, lastName, username, isPremium, isBot }
   * for numbers that are registered on Telegram. Empty array if none are found or on error.
   */
  checkPhoneNumbers: async (phoneNumbers: string): Promise<Array<{
    phone: string;
    userId: string;
    firstName?: string;
    lastName?: string;
    username?: string;
    isPremium?: boolean;
    isBot?: boolean;
  }>> => {
    if (!phoneNumbers?.trim()) return [];
    const rawNumbers = phoneNumbers.trim().split(/[\s\n,，]+/).filter(Boolean);
    if (rawNumbers.length === 0) return [];

    try {
      const contacts = rawNumbers.map((phone, index) => ({
        clientId: String(index + 1),
        phone: phone.replace(/\D/g, ''),
        firstName: '',
        lastName: '',
      }));

      const result = await callApi('fetchImportedContacts', { contacts });
      if (!result || !result.importedContacts.length || !result.users?.length) {
        return [];
      }

      const clientIdToPhone = new Map<string, string>();
      for (let i = 0; i < rawNumbers.length; i += 1) {
        clientIdToPhone.set(String(i + 1), rawNumbers[i]);
      }

      const found = result.importedContacts.map((imp) => {
        const user = result.users.find((u) => u.id === imp.userId);
        return {
          phone: clientIdToPhone.get(imp.clientId) || '',
          userId: imp.userId,
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          username: user?.usernames?.[0]?.username || '',
          isPremium: user?.isPremium || false,
          isBot: user?.type === 'userTypeBot' || false,
        };
      });

      return found;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('globalApi.checkPhoneNumbers error', err);
      return [];
    }
  },
};

// Expose on globalThis and window for compatibility
try {
  if (typeof globalThis !== 'undefined') {
    // attach non-enumerable to avoid accidental iterations
    Object.defineProperty(globalThis, 'TelegramExposedApi', {
      configurable: true,
      writable: false,
      value: api,
    });
  }
} catch (e) {
  // ignore
}

// Auto-initialize broadcast tasks from localStorage on module load
initBroadcastTasks();

export default api;
